import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import useLoadingOverlay from '@/hooks/useLoadingOverlay';
import useGoogleSignUp from '@/hooks/useGoogleSignUp';
import { googleSignupScopesQueryOptions } from '@/lib/query-options';
import { Card, CardContent, CardDescription, CardFooter, CardHeader } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import logo from '/logo_lightbg.svg';
import { GoogleButton } from '@/components/glitch-ui/google-button';
import { EmailSignupForm } from '@/components/auth/EmailSignupForm';
import { EmailSigninForm } from '@/components/auth/EmailSigninForm';
import { useEmailSignup, useEmailSignin } from '@/hooks/useEmailAuth';
import useGoogleSignIn from '@/hooks/useGoogleSignIn';
import { safeAuthRedirect } from '@/lib/auth-redirect';

export const Route = createFileRoute('/signup')({
  loader: ({ context }) => context.queryClient.ensureQueryData(googleSignupScopesQueryOptions()),
  component: Signup,
});

function Signup() {
  const navigate = useNavigate();
  
  // Check URL params for auth method
  const searchParams = new URLSearchParams(window.location.search);
  const initialAuthMethod = (searchParams.get('auth') as 'google' | 'email') || 'google';
  
  const [authMethod, setAuthMethod] = useState<'google' | 'email'>(initialAuthMethod);
  const [activeTab] = useState<'signin' | 'signup'>('signup');

  const { signupWithGoogle, isLoading: isGoogleSignupLoading } = useGoogleSignUp({
    redirectUrl: '/campaigns',
  });
  
  const { signInWithGoogle, isLoading: isGoogleSigninLoading } = useGoogleSignIn();
  
  const { isLoading: isEmailSignupLoading } = useEmailSignup();
  const { isLoading: isEmailSigninLoading } = useEmailSignin();
  
  const isLoading = isGoogleSignupLoading || isGoogleSigninLoading || isEmailSignupLoading || isEmailSigninLoading;
  useLoadingOverlay(isLoading);

  return (
    <div className='flex flex-col items-center mt-[148px]'>
      <img src={logo} alt='logo' className='h-[60px] mb-8' />
      <Card className='w-[480px] h-fit'>
        <Tabs value={activeTab} onValueChange={(value) => {
          if (value === 'signin') {
            // Carry the resume path across the tab switch (#6377).
            navigate({ to: '/signin', search: { auth: authMethod, redirect: safeAuthRedirect() ?? undefined } });
          }
        }} className="w-full">
          <TabsList className="grid w-full grid-cols-2 h-[60px] rounded-b-none">
            <TabsTrigger value="signin" className="text-base font-semibold h-[140%] data-[state=active]:shadow-none">Sign In</TabsTrigger>
            <TabsTrigger value="signup" className="text-base font-semibold h-[140%] data-[state=active]:shadow-none">Sign Up</TabsTrigger>
          </TabsList>
        </Tabs>
        <CardHeader className='px-8 pt-6'>
          <CardDescription className='text-gray-700 text-base py-4'>
            Glitch requires a Google Ads account to create and deploy campaigns. When Google asks for permissions, make sure the Google Ads checkbox is ticked. It is required and left unticked by default.
          </CardDescription>
        </CardHeader>
        <CardContent className='px-8 pb-8 space-y-4'>
          <Tabs value={activeTab} className="w-full">
            <TabsContent value="signin" className="space-y-4">
              {authMethod === 'google' ? (
                <GoogleButton onClick={signInWithGoogle} text="Sign in with Google" />
              ) : (
                <EmailSigninForm />
              )}
            </TabsContent>
            <TabsContent value="signup" className="space-y-4">
              {authMethod === 'google' ? (
                <GoogleButton onClick={signupWithGoogle} text="Sign up with Google" />
              ) : (
                <EmailSignupForm />
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
        <CardFooter className='text-gray-500 pb-6 px-8'>
          <p className='text-xs'>
            This service uses third party data, such as OpenAI to process and analyze data. By signing in, you acknowledge that your data may be processed by third parties in accordance with their{' '}
            <a href='URL-to-Privacy-Policy' className='underline'>
              Privacy Policy
            </a>
            . You can also{' '}
            {authMethod === 'google' ? (
              <button 
                onClick={() => {
                  setAuthMethod('email');
                  const newParams = new URLSearchParams(window.location.search);
                  newParams.set('auth', 'email');
                  window.history.replaceState({}, '', `${window.location.pathname}?${newParams}`);
                }}
                className='underline'
              >
                sign up with email
              </button>
            ) : (
              <button 
                onClick={() => {
                  setAuthMethod('google');
                  const newParams = new URLSearchParams(window.location.search);
                  newParams.set('auth', 'google');
                  window.history.replaceState({}, '', `${window.location.pathname}?${newParams}`);
                }}
                className='underline'
              >
                sign up with Google
              </button>
            )}
            , though this offers more limited functionality.
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}